import { Button } from "devextreme-react/button";
import { Column, DataGrid, Editing, Paging, Selection } from "devextreme-react/data-grid";
import { SelectBox } from "devextreme-react/select-box";
import { TextBox } from "devextreme-react/text-box";
import { Box, Item } from "devextreme-react/box";
import Popup, { ToolbarItem } from "devextreme-react/popup";
import { useContext, useRef, useState } from "react";
import { useGetProductLines, useGetProductTypes } from "../../../../api/product";
import { Field } from "../../../../layouts";
import { RetailOrderContext } from "../context/RetailOrderContext";
import { apiSearchChangeOrderProducts } from "../api/search-change-order-products"
import { showErrorMessage } from "../../../../utils/messages";
import { ROCatalogProduct } from "../types/ROCatalogProduct"
import { alert } from "devextreme/ui/dialog";
import { ProductCatalog, ProductLine } from "../../../../types/enums";
import { LoadPopupPanel } from "../../../../components";

export default function ChangeOrderProductPopup(props: {
	visible: boolean,
	onPopupHidden: () => void,
	onProductsAdded: (newProducts: ROCatalogProduct[]) => void
}) {

	const { order } = useContext(RetailOrderContext);

	const [processing, setProcessing] = useState(false);

	const [searchCriteria, setSearchCriteria] = useState<{
		productLineID: number, productTypeID?: number,
		sku?: string, productDescription?: string
	}>({
		productLineID: order.details.productLineID
	});

	const [searchResults, setSearchResults] = useState<ROCatalogProduct[]>([]);

	const productLines = useGetProductLines(props.visible);
	const productTypes = useGetProductTypes(ProductCatalog.Retail, searchCriteria.productLineID, props.visible);

	const searchResultsRef = useRef<DataGrid>(null);

	return (
		<Popup
			title="Add Change Order Product"
			visible={props.visible}
			hideOnOutsideClick={true}
			onHiding={() => props.onPopupHidden()}
		>
			<ToolbarItem
				options={{
					icon: 'dx-icon dx-icon-close',
					onClick: function () {
						props.onPopupHidden();
					}
				}}
				widget="dxButton"
				location="after"
			/>
			<Box className="product-search-filter field-label-25">
				<Item ratio={1}>
					<Field label={'Product Line'}>
						<SelectBox
							showClearButton={false}
							dataSource={productLines.data}
							valueExpr={'productLineID'}
							displayExpr={'description'}
							value={searchCriteria.productLineID}
							onValueChanged={(e) => {
								setSearchCriteria({ ...searchCriteria, productLineID: e.value });
								setSearchResults([] as ROCatalogProduct[]);
							}}
						/>
					</Field>
					<Field label={'Product Type'}>
						<SelectBox
							showClearButton={true}
							dataSource={productTypes.data}
							valueExpr={'productTypeID'}
							displayExpr={'description'}
							value={searchCriteria.productTypeID}
							onValueChanged={(e) => setSearchCriteria({ ...searchCriteria, productTypeID: e.value })}
						/>
					</Field>
				</Item>
				<Item ratio={1}>
					<Field label={'SKU'}>
						<TextBox
							showClearButton={true}
							value={searchCriteria.sku}
							onValueChanged={(e) => setSearchCriteria({ ...searchCriteria, sku: e.value })}
						></TextBox>
					</Field>
					<Field label={'Product Description'}>
						<TextBox
							showClearButton={true}
							value={searchCriteria.productDescription}
							onValueChanged={(e) => setSearchCriteria({ ...searchCriteria, productDescription: e.value })}
						></TextBox>
					</Field>
				</Item>
				<Item ratio={0} baseSize={100}>
					<Button
						text="Search"
						onClick={() => {
							setProcessing(true);
							apiSearchChangeOrderProducts(
								order.details.roDate,
								order.details.proxyDealerCompanyID ?? order.details.dealerCompanyID,
								searchCriteria.productLineID,
								order.details.customerZip,
								searchCriteria.productTypeID,
								searchCriteria.sku,
								searchCriteria.productDescription)
								.then((result) => {
									if (result.isSuccess)
										setSearchResults(result.products);
									else
										showErrorMessage(result.errorMessage!);
								})
								.finally(() => setProcessing(false));
						}}>
					</Button>
				</Item>
			</Box>
			<DataGrid
				ref={searchResultsRef}
				className="product-search-results"
				dataSource={searchResults}
				height={'76%'}
				wordWrapEnabled={true}
				keyExpr={'productID'}
				repaintChangesOnly={true}
				onSelectionChanged={(e) => {
					// Clear extended description and quantity for deselected rows
					e.currentDeselectedRowKeys.forEach(productID => {
						const rowIndex = e.component.getRowIndexByKey(productID);
						e.component.cellValue(rowIndex, "extendedDescription", undefined);
						e.component.cellValue(rowIndex, "quantity", 1);
						e.component.saveEditData().then(() => e.component.repaintRows([rowIndex]));
					});
				}}
			>
				<Paging enabled={false}></Paging>
				<Selection mode="multiple" allowSelectAll={false} />
				<Editing allowUpdating={true} mode={'cell'} refreshMode={'repaint'}></Editing>
				<Column
					dataField="sku"
					caption="House PO SKU"
					dataType="string"
					allowEditing={false}
				/>
				<Column
					dataField="mfrModel"
					caption="MFR SKU"
					dataType="string"
					allowEditing={false}
				/>
				<Column
					dataField="description"
					caption="Description"
					dataType="string"
					width={300}
					allowEditing={false}
					cellRender={(cell) => {
						if (cell.data.descriptionRequired)
							return <>
								{cell.data.description}
								<TextBox className="option-description" width={'100%'}
									maxLength={498 - cell.data.description.length}
									onValueChanged={(e) => {
										cell.component.cellValue(cell.component.getRowIndexByKey(cell.data.productID),
											"extendedDescription", e.value);
										cell.component.saveEditData();
									}} />
							</>
						else
							return <>{cell.data.description}</>
					}}
				/>
				<Column dataField={'extendedDescription'} visible={false}></Column>
				{
					searchCriteria.productLineID !== ProductLine.Cabinets &&
					<Column
						dataField="thickness"
						caption="Thickness"
						dataType="string"
						allowEditing={false}
					/>
				}
				{
					searchCriteria.productLineID !== ProductLine.Cabinets &&
					<Column
						dataField="dimensions"
						caption="Dimensions"
						dataType="string"
						allowEditing={false}
					/>
				}
				{
					searchCriteria.productLineID !== ProductLine.Cabinets &&
					<Column
						dataField="squareFootage"
						caption="Square Footage"
						dataType="string"
						allowEditing={false}
					/>
				}
				{
					searchCriteria.productLineID !== ProductLine.Cabinets &&
					<Column
						dataField="distributorName"
						caption="Distributor"
						dataType="string"
						allowEditing={false}
					/>
				}
				{
					searchCriteria.productLineID !== ProductLine.Cabinets &&
					<Column
						caption="Image"
						width={70}
						cellRender={(cell) => {
							if (cell.data.swatchThumbnail)
								return <img src={'data:image/png;base64, ' + cell.data.swatchThumbnail} className='swatch-thumbnail' />
							else
								return <></>
						}}
					/>
				}
				{
					searchCriteria.productLineID === ProductLine.Cabinets &&
					<Column
						dataField="uom"
						caption="UOM"
						dataType="string"
						allowEditing={false}
					/>
				}
				<Column
					dataField={'quantity'}
					caption="Quantity"
					width={100}
					dataType={'number'}
					editorOptions={{ showSpinButtons: true, min: 0, format: "##0.###" }}
					showEditorAlways={true}
				/>
			</DataGrid>
			<ToolbarItem
				options={{
					type: 'normal',
					stylingMode: 'contained',
					text: 'Add Items',
					onClick: () => {
						const selectedRows = searchResultsRef.current?.instance.getSelectedRowsData() as ROCatalogProduct[];

						let isValid = true;

						for (let row of selectedRows) {
							if (row.descriptionRequired && !row.extendedDescription) {
								alert("Explanation of misc/custom charge required, i.e.: 3 support brackets for overhang support.", "Error");
								isValid = false;
								break;
							}

							if (row.quantity === 0) {
								alert("Quantity must be greater than 0", "Error");
								isValid = false;
								break;
							}
						}

						if (isValid) {
							props.onProductsAdded(selectedRows);
							props.onPopupHidden();
							searchResultsRef.current?.instance.clearSelection();
						}
					}
				}}
				toolbar={'bottom'}
				widget="dxButton"
				location="after"
			>
			</ToolbarItem>
			<ToolbarItem
				options={{
					type: 'normal',
					stylingMode: 'contained',
					text: 'Cancel',
					onClick: () => props.onPopupHidden()
				}}
				toolbar={'bottom'}
				widget="dxButton"
				location="after"
			>
			</ToolbarItem>
			<LoadPopupPanel visible={productLines.isLoading || productTypes.isLoading || processing}></LoadPopupPanel>
		</Popup>
	)
}

